import clsx from 'clsx';
import * as React from 'react';
import { usePress } from 'react-aria';

import type * as api from '@gitbook/api-client';
import { type Collection, resolveContentIcon, type Space } from '@gitbook/app-database';
import { Emoji } from '@gitbook/spine-react';

import { getImageResizedURL } from '../../utils';

const EMOJI_BACKGROUND_FACTOR = 2;
const BORDERED_IMAGE_ICON_SIZE_FACTOR = 0.7;

/**
 * A boxed emoji is 70% of its box, so the 1px nudge `Emoji` applies (tuned for text-sized emoji)
 * pushes it visibly low; an offset relative to the glyph keeps it centred at every size.
 */
const BOXED_EMOJI_STYLE: React.CSSProperties = { transform: 'translateY(0.0625em)' };

export type ContentIconProps = {
    content: Space | api.Space | Collection;
    /** Whether the icon should be rendered with a background or not, defaults to true */
    withBackground?: boolean;
    size?: number;
    className?: string;
};

/**
 * Icon for content.
 */
export function ContentIcon(props: ContentIconProps): React.ReactElement {
    const { content, size, className, withBackground = true } = props;

    const { iconURL, emoji } = resolveContentIcon(content);

    return (
        <ControlledContentIcon
            iconURL={iconURL}
            emoji={emoji}
            fallbackText={content.title}
            size={size}
            className={className}
            withBackground={withBackground}
        />
    );
}

/**
 * Icon for content,when directly passing iconURL / emoji.
 */
export function ControlledContentIcon({
    ref,
    ...props
}: {
    iconURL: string | undefined;
    emoji: string | undefined;
    fallbackText: string;
    /** Whether the icon should be rendered with a background or not, defaults to true */
    withBackground?: boolean;
    size?: number;
    className?: string;
    style?: React.CSSProperties;
    itemStyle?: React.CSSProperties;
    /** When passed, a role and focus states will also apply */
    onPress?: () => void;
} & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const {
        iconURL,
        emoji,
        fallbackText,
        size,
        className,
        withBackground = true,
        onPress,
        style,
        itemStyle,
    } = props;
    const imgSize: React.CSSProperties = size
        ? { width: size, height: size }
        : { width: '1em', height: '1em' };
    const imageItemSize = getImageItemSize(size, withBackground);
    const emojiSize = getEmojiSize(size, withBackground);
    const { pressProps } = usePress({ onPress });

    return (
        <div
            ref={ref}
            className={clsx(
                'relative flex items-center justify-center rounded-sm transition',
                onPress && 'cursor-pointer focus-visible:focus-ring',
                withBackground && 'overflow-hidden border border-base',
                withBackground && !style?.background && 'bg-muted',
                className
            )}
            {...(onPress && {
                role: 'button',
                tabIndex: 0,
                ...pressProps,
            })}
            style={
                {
                    ...imgSize,
                    '--emoji-bg-factor': EMOJI_BACKGROUND_FACTOR,
                    ...style,
                } as React.CSSProperties
            }
        >
            {iconURL ? (
                <img
                    src={getImageResizedURL(iconURL, {
                        width: size,
                        height: size,
                        fit: 'contain',
                    })}
                    className="block rounded-sm object-contain object-center"
                    style={{
                        ...imgSize,
                        ...imageItemSize,
                        ...itemStyle,
                    }}
                    alt=""
                />
            ) : emoji && emojiSize ? (
                <>
                    <Emoji
                        code={emoji}
                        size={emojiSize}
                        style={withBackground ? BOXED_EMOJI_STYLE : undefined}
                    />
                    {withBackground && (
                        <div className="absolute flex">
                            <Emoji
                                code={emoji}
                                size={size ? size * EMOJI_BACKGROUND_FACTOR : '2em'}
                                className="opacity-20 dark:opacity-5"
                                style={{
                                    // Filter does not work with TW for a reason I ignore, maybe !important
                                    filter: 'blur(calc(var(--emoji-bg-factor, 2) * 8px)) contrast(110%) hue-rotate(10deg) brightness(1.2) saturate(1.2)',
                                }}
                            />
                        </div>
                    )}
                </>
            ) : emojiSize ? (
                <FallbackIcon fallbackText={fallbackText} size={size ?? '1em'} style={itemStyle} />
            ) : null}
        </div>
    );
}

function FallbackIcon(props: {
    fallbackText: string;
    size: number | string;
    style?: React.CSSProperties;
}) {
    const { size, fallbackText, style } = props;
    const letter = fallbackText.slice(0, 1).toUpperCase();
    return (
        <svg
            viewBox="0 0 100 100"
            width={size}
            height={size}
            xmlns="http://www.w3.org/2000/svg"
            style={style}
        >
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dy=".35em"
                fontSize="80"
                className="fill-[currentColor] font-system font-bold"
            >
                {letter}
            </text>
        </svg>
    );
}

function getImageItemSize(
    iconSize: number | undefined,
    withBackground: boolean
): React.CSSProperties | undefined {
    if (withBackground && iconSize) {
        const itemSize = Math.round(iconSize * BORDERED_IMAGE_ICON_SIZE_FACTOR);
        return { width: itemSize, height: itemSize };
    }

    return undefined;
}

function getEmojiSize(iconSize: number | undefined, withBackground: boolean): number | string {
    if (withBackground) {
        return iconSize ? iconSize * 0.7 : '0.7em';
    }
    return iconSize ?? '1em';
}
